import { CenteredEmptyState } from '@components/display/emptyStates';
import { Box } from '@mui/material';
import { NotificationItemCard } from '@notifications/components/display/cards';
import { useNotificationsBoxContext } from '@notifications/contexts';
import {
    useGetNotificationStreamQuery,
    useGetPaginatedNotificationsQuery,
    useLazyGetPaginatedNotificationsQuery,
    useMarkNotificationAsReadMutation,
} from '@notifications/services';
import { LoadableNotification } from '@notifications/types';
import { handleError } from '@utils/common';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { List } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

const PAGE_SIZE = 6;

export const UnreadNotificationsBox = forwardRef((props, ref: React.Ref<HTMLDivElement>) => {
    const {
        unreadNotificationsState: [notifications, setNotifications] = [[], () => {}],
        setUnreadNotificationsTotalCount,
        isReadActionRunning,
        setIsReadActionRunning,
        readNotificationsState: [_, setReadNotifications] = [[], () => {}],
        processedStreamIdsRef,
    } = useNotificationsBoxContext();
    //#region Infinite Loader State
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isNextPageLoading, setIsNextPageLoading] = useState(false);
    const [page, setPage] = useState<number>(1);
    //#endregion

    const intl = useIntl();

    //#region Infinite Loader queries
    const [loadMoreNotifications, { isFetching: isFetchingMoreNotifications }] =
        useLazyGetPaginatedNotificationsQuery();
    const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
    const { data: streamNotifications } = useGetNotificationStreamQuery();
    const { data: hasNotificationsData } = useGetPaginatedNotificationsQuery({
        is_read: false,
        page: 1,
        page_size: 1,
    });
    //#endregion

    //#region Infinite Loader variables
    const rowCount = hasNextPage ? notifications.length + 1 : notifications.length;
    //#endregion

    //#region Infinite Loader functions
    const isRowLoaded = useCallback(
        (index: number) => {
            return !hasNextPage || index < notifications.length;
        },
        [hasNextPage, notifications]
    );

    const handleLoadMoreNotifications = useCallback(async () => {
        if (isNextPageLoading || !hasNextPage) {
            return;
        }

        setIsNextPageLoading(true);

        try {
            const response = await loadMoreNotifications({
                is_read: false,
                page,
                page_size: PAGE_SIZE,
            }).unwrap();
            if (response && response.results.length > 0) {
                setNotifications((prevNotifications) => {
                    const existingIds = new Set(prevNotifications.map((notification) => notification.id));
                    const uniquePageResults = response.results.filter((notification) => {
                        if (existingIds.has(notification.id)) {
                            return false;
                        }

                        existingIds.add(notification.id);
                        return true;
                    });
                    return [...prevNotifications, ...uniquePageResults];
                });
                setPage((prevPage) => prevPage + 1);
                setUnreadNotificationsTotalCount((prevCount) => Math.max(prevCount, response.count));
                setHasNextPage(response.results.length === PAGE_SIZE);
            } else {
                setHasNextPage(false);
            }
        } catch (error) {
            console.error('Error loading more notifications:', error);
            setHasNextPage(false);
        } finally {
            setIsNextPageLoading(false);
        }
    }, [isNextPageLoading, hasNextPage, page, setUnreadNotificationsTotalCount]);

    const onRowsRendered = useInfiniteLoader({
        isRowLoaded,
        loadMoreRows: handleLoadMoreNotifications,
        rowCount,
        minimumBatchSize: PAGE_SIZE,
        threshold: 1,
    });
    //#endregion

    //#region Stream Notifications Effect
    useEffect(() => {
        if (!streamNotifications?.length) {
            return;
        }

        const unseenNotifications = streamNotifications.filter((notification) => {
            if (processedStreamIdsRef?.current?.has(notification.id)) {
                return false;
            }

            processedStreamIdsRef?.current?.add(notification.id);
            return true;
        });

        if (!unseenNotifications.length) {
            return;
        }

        setNotifications((prevNotifications) => {
            const existingIds = new Set(prevNotifications.map((notification) => notification.id));
            const newAtTop = unseenNotifications.filter((notification) => !existingIds.has(notification.id));
            if (!newAtTop.length) {
                return prevNotifications;
            }

            return [...newAtTop, ...prevNotifications];
        });

        setUnreadNotificationsTotalCount((prevCount) => {
            const baseCount = prevCount || hasNotificationsData?.count || 0;
            return baseCount + unseenNotifications.length;
        });
    }, [streamNotifications, hasNotificationsData?.count]);
    //#endregion

    const handleMarkAsRead = useCallback(
        async (id: LoadableNotification['id']) => {
            if (isReadActionRunning) {
                return;
            }

            setIsReadActionRunning(true);

            const movedNotification = notifications.find((notification) => notification.id === id);
            setNotifications((prevNotifications) => prevNotifications.filter((notification) => notification.id !== id));

            if (!movedNotification) {
                setIsReadActionRunning(false);
                return;
            }

            const readNotification = { ...movedNotification, is_read: true };

            setReadNotifications((prevReadNotifications) => {
                if (prevReadNotifications.some((notification) => notification.id === id)) {
                    return prevReadNotifications;
                }

                return [readNotification, ...prevReadNotifications];
            });

            setUnreadNotificationsTotalCount((prevCount) => Math.max(0, prevCount - 1));

            try {
                await markNotificationAsRead({ notification_id: id }).unwrap();
            } catch (error) {
                // Revert optimistic update if server request fails.
                setNotifications((prevNotifications) => {
                    if (prevNotifications.some((notification) => notification.id === id)) {
                        return prevNotifications;
                    }

                    return [{ ...readNotification, is_read: false }, ...prevNotifications];
                });
                setReadNotifications((prevReadNotifications) =>
                    prevReadNotifications.filter((notification) => notification.id !== id)
                );
                setUnreadNotificationsTotalCount((prevCount) => prevCount + 1);
                console.error('Error marking notification as read:', error);
                handleError(
                    intl.formatMessage({
                        id: 'notifications.markAsReadError',
                        defaultMessage: 'Failed to mark notification as read.',
                    }),
                    error
                );
            } finally {
                setIsReadActionRunning(false);
            }
        },
        [
            intl,
            isReadActionRunning,
            markNotificationAsRead,
            setIsReadActionRunning,
            setReadNotifications,
            setUnreadNotificationsTotalCount,
            setNotifications,
            notifications,
        ]
    );

    return (
        <Box height={{ xs: '100%', md: '400px' }} width="100%" ref={ref} {...props}>
            <List
                onRowsRendered={onRowsRendered}
                rowComponent={NotificationItemCard}
                rowCount={rowCount}
                rowHeight={67}
                rowProps={{ notifications, hasNextPage, onRead: handleMarkAsRead, isReadActionRunning }}
                style={notifications.length !== 0 && !isFetchingMoreNotifications ? { height: '100%' } : undefined}
            />
            {notifications.length === 0 && !isFetchingMoreNotifications && (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="notifications.allCaughtUp"
                            defaultMessage="All caught up! No new notifications."
                        />
                    }
                    height={{ xs: '100%', md: '400px' }}
                />
            )}
        </Box>
    );
});
